import { createContext, useContext, useEffect, useState } from 'react';

// ---------------------------------------------------------------------------
// Theme. Dark is the default night palette; light is tuned for sunlight
// readability (bright surfaces, near-black text, strong borders). Alert colors
// stay vivid in both; a few semantic colors get darker light-mode variants.
// Shared by the radar (App.js) and the auth/settings pages (authStyles.js).
// ---------------------------------------------------------------------------
export const THEME_KEY = 'tcas_theme';

export const THEMES = {
  dark: {
    stage: '#000', bezel: '#1c1c1e', bezelRing: '#000', shadow: 'rgba(0,0,0,0.7)',
    bg: '#111', panel: '#0d0d0d', surface: '#1a1a1a', surfaceActive: '#1e1e1e',
    border: '#2a2a2a', borderSoft: '#222', hairline: '#1e1e1e', compassFill: '#0a0a0a',
    textStrong: '#fff', text: '#ccc', textMuted: '#888',
    cardinal: '#666', faint: '#444', ringText: '#444',
    clearIcon: '#2a2a2a', clearText: '#333', clock: '#ddd',
    north: '#cc3333', pilot: '#ffffff', pilotOutline: '#000',
    altUp: '#ff6666', altDown: '#66aaff',
    overlay: 'rgba(0,0,0,0.85)', overlayText: '#fff', overlaySub: '#666', splash: '#555',
  },
  light: {
    stage: '#c7ccd2', bezel: '#aeb4bc', bezelRing: '#9aa0a8', shadow: 'rgba(0,0,0,0.35)',
    bg: '#ffffff', panel: '#eceff3', surface: '#f3f5f8', surfaceActive: '#d6dbe2',
    border: '#b2b8c0', borderSoft: '#d2d7dd', hairline: '#c6cbd2', compassFill: '#ffffff',
    textStrong: '#000', text: '#1a1a1a', textMuted: '#3a3a3a',
    cardinal: '#333', faint: '#888', ringText: '#777',
    clearIcon: '#c6cbd2', clearText: '#9aa0a8', clock: '#000',
    north: '#cc0000', pilot: '#000', pilotOutline: '#fff',
    altUp: '#cc0000', altDown: '#0048c8',
    overlay: 'rgba(245,247,250,0.92)', overlayText: '#000', overlaySub: '#555', splash: '#888',
  },
};

function loadTheme() {
  // Default to Light: only an explicitly-stored 'dark' opts out. Existing users
  // who toggled keep their stored choice; fresh sessions start Light (white).
  try { return localStorage.getItem(THEME_KEY) === 'dark' ? 'dark' : 'light'; }
  catch { return 'light'; }
}

const ThemeContext = createContext(null);

export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(loadTheme);
  useEffect(() => {
    try { localStorage.setItem(THEME_KEY, theme); } catch { /* ignore */ }
  }, [theme]);
  const toggle = () => setTheme(t => (t === 'dark' ? 'light' : 'dark'));
  const value = { theme, colors: THEMES[theme] || THEMES.dark, toggle, setTheme };
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  return useContext(ThemeContext);
}

// Small Dark/Light pill used in the corner of the auth/settings cards.
export function ThemeToggle({ style }) {
  const { theme, colors, toggle } = useTheme();
  return (
    <button
      type="button"
      onClick={toggle}
      title="Toggle dark / light"
      style={{
        background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 999,
        color: colors.text, fontSize: 12, fontWeight: 700, padding: '5px 10px', cursor: 'pointer',
        ...style,
      }}
    >
      {theme === 'dark' ? '☾ Dark' : '☀ Light'}
    </button>
  );
}
